import { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { adminJournalAPI } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, CalendarClock, AlertTriangle } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
const TONE = {
  attendu: 'bg-emerald-100 text-emerald-800',
  en_attente: 'bg-sky-100 text-sky-800',
  paye_sans_ipn: 'bg-amber-100 text-amber-900',
  echec_confirme: 'bg-red-100 text-red-800',
  suspendu: 'bg-red-100 text-red-800',
  resilie: 'bg-slate-200 text-slate-800',
  retard_a_verifier: 'bg-amber-100 text-amber-900',
  verification_impossible: 'bg-slate-100 text-slate-700',
};

export const RenewalAlertCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [rem, setRem] = useState(null);
  const load = async () => { try { setData((await adminJournalAPI.renewals()).data); } catch { setData(false); } try { setRem((await adminJournalAPI.reminderStatus()).data); } catch { setRem(null); } };
  useEffect(() => { load(); }, []);
  const check = async () => {
    setBusy('check');
    try { setData((await adminJournalAPI.checkRenewals()).data); toast.success('État des abonnements vérifié auprès de PayPal'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Vérification impossible'); } finally { setBusy(''); }
  };
  const sync = async (id) => {
    setBusy(id);
    try { const r = await adminJournalAPI.syncRenewal(id); toast.success(`Accès prolongé jusqu'au ${fmt(r.data.access_until)} (preuve PayPal)`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Synchronisation refusée'); } finally { setBusy(''); }
  };
  const dryRun = async () => {
    setBusy('dry');
    try { const r = await adminJournalAPI.reminderDryRun(); toast.success(`Simulation : ${r.data.candidates.length} abonné(s) dans la fenêtre J-45, aucun email envoyé`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Simulation impossible'); } finally { setBusy(''); }
  };
  if (data === null) return null;
  const s = data ? data.summary || {} : {};
  const alerts = data ? (data.results || []).filter((r) => r.alert) : [];
  const hasAlert = alerts.length > 0;
  return (
    <Card className={hasAlert ? 'border-amber-400 bg-amber-50/40' : 'border-emerald-200'} data-testid="renewal-alert-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`inline-flex h-3 w-3 rounded-full ${hasAlert ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} data-testid="renewal-indicator" />
            <CalendarClock className={`h-5 w-5 ${hasAlert ? 'text-amber-700' : 'text-emerald-700'}`} />
            <div>
              <div className="font-semibold text-sm" data-testid="renewal-title">
                {data === false ? 'Suivi des renouvellements indisponible' : hasAlert ? `${alerts.length} renouvellement(s) à suivre` : 'Renouvellements : aucune anomalie'}
              </div>
              <div className="text-xs text-slate-600" data-testid="renewal-detail">
                {s.total ?? 0} abonnement(s) suivi(s) · attendus {s.attendu ?? 0} · en attente {s.en_attente ?? 0} · payés sans notification {s.paye_sans_ipn ?? 0} · échecs confirmés {s.echec_confirme ?? 0} · suspendus {s.suspendu ?? 0} · résiliés {s.resilie ?? 0}
                {data?.checked_at ? ` · vérifié le ${new Date(data.checked_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}` : ' · jamais vérifié'}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={check} disabled={!!busy} data-testid="renewal-check-btn"><RefreshCw className={`h-4 w-4 mr-1 ${busy === 'check' ? 'animate-spin' : ''}`} />Vérifier auprès de PayPal</Button>
        </div>
        {hasAlert && (
          <div className="space-y-2" data-testid="renewal-alerts-list">
            {alerts.map((r) => (
              <div key={r.subscription_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-white p-2 text-xs" data-testid={`renewal-row-${r.subscription_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${TONE[r.category] || TONE.verification_impossible}`}>{r.label}</span>
                  <span className="font-mono">{r.subscription_id}</span>
                  <span>{r.email}</span>
                  <span>· échéance {fmt(r.expected_at)}{r.overdue_days != null ? ` (+${r.overdue_days} j)` : ''}</span>
                  <span>· accès jusqu'au {fmt(r.access_until)}</span>
                  {r.paypal_status && <span>· PayPal : {r.paypal_status}{r.failed_payments_count ? `, ${r.failed_payments_count} échec(s)` : ''}</span>}
                </div>
                {r.category === 'paye_sans_ipn' && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => sync(r.subscription_id)} data-testid={`renewal-sync-${r.subscription_id}`}>Prolonger l'accès (preuve PayPal)</Button>}
              </div>
            ))}
          </div>
        )}
        {rem && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-white p-2 text-xs" data-testid="reminder-status">
            <span>
              Rappel informatif annuel J-{rem.days_before} : <strong data-testid="reminder-enabled">{rem.enabled ? 'ACTIVÉ' : 'désactivé'}</strong>
              {rem.last_run ? ` · dernière simulation ${new Date(rem.last_run.ran_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} : ${rem.last_run.candidates?.length ?? 0} candidat(s), ${rem.last_run.sent?.length ?? 0} envoyé(s)` : ' · jamais exécuté'}
            </span>
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={dryRun} data-testid="reminder-dry-run-btn">Simuler (sans envoi)</Button>
          </div>
        )}
        <p className="text-[11px] text-slate-500">Lecture seule auprès de PayPal. Aucun prélèvement n'est déclenché et aucun compte n'est désactivé automatiquement ; un retard de notification n'est jamais assimilé à un impayé avant le délai PayPal de 3 jours.</p>
      </CardContent>
    </Card>
  );
};
